import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Globe, Images, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { ChatInput } from 'features/chat/components/ChatInput';
import { ButtonIds, initialFeaturesState, type Attachment } from 'features/chat/types/chat';
import { useGetAgentById } from 'features/chat/hooks/use-agents';
import {
  invalidateAgentConversationList,
  prependAgentConversationToCache,
} from 'features/chat/utils/agent-sidebar-sync';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useModelStore } from 'state/store/chat/use-model-store';
import { useConfig } from 'hooks/use-config';
import { useIsMobile } from 'hooks/use-mobile';
import { useInviteAssistant } from 'features/group-chat/hooks/use-invite';
import type { InvitationPayload } from 'features/group-chat/services/group-services';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, InitialPromptType } from 'analytics/events';
import { Alignment, ButtonTypes, type ChatInputButton } from 'pages/chat/chat';
import { DEFAULT_MAIN_CHAT_AGENT_ID } from 'utils/constants/chat-constants';
import { LinkedScheduledJobDialog } from './components/linked-scheduled-job-dialog';
import { ScheduledJobList } from './components/scheduled-job-list';
import { useGetScheduledJobs } from './hooks/use-scheduled-jobs';
import { LIVE_STATUSES, type ScheduledJobStatus } from './services/scheduled-jobs.types';

/** "all" is the jobs a user can still act on: active and paused together. */
type ScheduledJobFilter = 'all' | ScheduledJobStatus;

const STATUS_FILTER_OPTIONS: {
  value: ScheduledJobFilter;
  labelKey: string;
  defaultLabel: string;
}[] = [
    { value: 'all', labelKey: 'ALL', defaultLabel: 'All' },
    { value: 'active', labelKey: 'ACTIVE', defaultLabel: 'Active' },
    { value: 'paused', labelKey: 'PAUSED', defaultLabel: 'Paused' },
    { value: 'completed', labelKey: 'COMPLETED', defaultLabel: 'Completed' },
    { value: 'expired', labelKey: 'EXPIRED', defaultLabel: 'Expired' },
    { value: 'failed', labelKey: 'FAILED', defaultLabel: 'Failed' },
  ];

export default function SchedulerPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { configurationSettings } = useConfig();
  const sendMessage = useChatStore((state) => state.sendMessage);
  const setActiveConversationId = useChatStore((state) => state.setActiveConversationId);
  const selectedModel = useModelStore((state) => state.selectedModel);
  const inviteAssistantMutation = useInviteAssistant();
  const queryClient = useQueryClient();
  const models = useModelStore((state) => state.models);
  const [statusFilter, setStatusFilter] = useState<ScheduledJobFilter>('all');

  const [isFeatureSelected, setIsFeatureSelected] =
    useState<Record<ButtonIds, boolean>>(initialFeaturesState);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedFolderIdsBySource, setSelectedFolderIdsBySource] = useState<
    Record<string, string[]>
  >({});
  const [isSourcesToolActive, setIsSourcesToolActive] = useState(false);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);

  // Agent selection: mirrors chat.tsx — the picked agent (not a model) answers the
  // new conversation, and its model is shown/locked in the input's model selector.
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const { data: selectedAgentData, isError: isAgentDetailError } = useGetAgentById(
    { id: selectedAgentId ?? '' },
    { enabled: !!selectedAgentId }
  );
  const selectedAgentLlm = selectedAgentData?.agent?.llm_config;
  const agentModelLoading = !!selectedAgentId && !selectedAgentData && !isAgentDetailError;
  const agentUploadModel = useMemo(() => {
    if (!selectedAgentLlm) return null;
    const { model_id, model_name } = selectedAgentLlm;
    const normalizedName = model_name?.toLowerCase();
    return (
      models.find(
        (m) =>
          (model_id && m.id === model_id) ||
          (model_name && (m.id === model_name || m.name === model_name)) ||
          (normalizedName &&
            (m.id.toLowerCase() === normalizedName || m.name.toLowerCase() === normalizedName)),
      ) ?? null
    );
  }, [selectedAgentLlm, models]);
  const agentModelLabel = agentUploadModel?.name ?? selectedAgentLlm?.model_name ?? null;

  const selectedStatusOption =
    STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter) ??
    STATUS_FILTER_OPTIONS[0];

  // Recent: always the 3 newest active or paused jobs by createdDate, so a job
  // toggled from this section stays in it.
  const {
    data: recentData,
    isLoading: isRecentLoading,
    isError: isRecentError,
    isFetching: isRecentFetching,
  } = useGetScheduledJobs({
    status: LIVE_STATUSES,
    skip: 0,
    take: 3,
    sort: 'createdDate',
    order: 'desc',
  });

  // Filtered list. "All" is active + paused, newest first: a paused job keeps
  // its old next-run time, so ordering by next run would be misleading there.
  const {
    data: statusData,
    isLoading: isStatusLoading,
    isError: isStatusError,
    isFetching: isStatusFetching,
  } = useGetScheduledJobs(
    statusFilter === 'all'
      ? { status: LIVE_STATUSES, skip: 0, take: 25, sort: 'createdDate', order: 'desc' }
      : {
          status: statusFilter,
          skip: 0,
          take: 25,
          sort: statusFilter === 'active' ? 'nextRunAt' : 'lastRunAt',
          order: statusFilter === 'active' ? 'asc' : 'desc',
        }
  );

  const recentJobs = recentData?.items ?? [];
  const statusJobs = statusData?.items ?? [];

  // ChatInput only renders its footer toolbar (plus menu, tools, mic, send)
  // when inputButtons is non-empty — same set as project-page/chat.
  const inputButtons = useMemo<ChatInputButton[]>(
    () => [
      {
        id: ButtonIds.SEARCH,
        label: t('SEARCH'),
        icon: <Globe size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: isFeatureSelected[ButtonIds.CREATE_IMAGE] || selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.SEARCH],
      },
      {
        id: ButtonIds.CREATE_IMAGE,
        label: t('CREATE_IMAGE'),
        icon: <Images size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: selectedSources.length > 0,
        isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
      },
    ],
    [isMobile, isFeatureSelected, t, selectedSources.length]
  );

  // Detach from any previous chat so this landing composer is not bound to it.
  useEffect(() => {
    setActiveConversationId(null);
  }, [setActiveConversationId]);

  // Same flow as project-page/custom-gpt-page: create a conversation, send the
  // message through the chat store, then redirect to the chat page.

  const handleSchedule = async (content: string, files: Attachment[]) => {
    if (!content.trim() && files.length === 0) return;

    setActiveConversationId(null);

    const payload: InvitationPayload = selectedAgentId
      ? {
        invitationType: 'ADD_USER',
        agentId: selectedAgentId,
      }
      : {
        invitationType: 'ADD_USER',
        agentId: DEFAULT_MAIN_CHAT_AGENT_ID,
        assistantId: selectedModel?.id,
      };

    try {
      const response = await inviteAssistantMutation.mutateAsync(payload);
      if (!response?.conversationId) return;

      const newConversationId = response.conversationId;
      trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));

      await sendMessage(
        content,
        configurationSettings,
        newConversationId,
        files,
        isFeatureSelected,
        false, // isGroupChatParticipants
        undefined, // parentIds
        undefined, // sources
        false, // isRegenerate
        undefined, // originalRegenerationMessageId
        undefined, // mentions
        undefined, // parentUserGenerationId
        false, // isIncognitoChat
        undefined, // customGptId
        selectedTools,
        undefined, // selectedSkillIds
        selectedAgentId ?? undefined,
      );

      if (selectedAgentId) {
        prependAgentConversationToCache(
          queryClient,
          selectedAgentId,
          configurationSettings?.blocksKey || '',
          newConversationId
        );
        invalidateAgentConversationList(queryClient);
      }

      navigate(`/chat/${newConversationId}`, {
        state: {
          preservedFeatures: isFeatureSelected,
          preservedSources: selectedSources,
          preservedFolderIdsBySource: selectedFolderIdsBySource,
          preservedSourcesToolActive: isSourcesToolActive,
          preservedTools: selectedTools,
        },
      });
    } catch (error) {
      console.error('Failed to start chat from scheduler:', error);
    }
  };

  return (
    <div className="flex min-h-0 flex-col overflow-y-auto overflow-x-hidden">
      <div className="mx-auto w-full max-w-[720px] px-4 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <h1 className="text-2xl font-medium leading-heading tracking-tight text-grey-900 dark:text-foreground">
              {t('SCHEDULED', { defaultValue: 'Scheduled' })}
            </h1>
            <p className="text-sm font-normal leading-heading text-grey-900 dark:text-muted-foreground">
              {t('SCHEDULER_PAGE_SUBTITLE', {
                defaultValue: 'Ask Klax! to schedule tasks, set reminders, or monitor for updates.',
              })}
            </p>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="h-auto shrink-0 gap-1 rounded-lg border border-transparent bg-grey-50 px-3 py-3 text-sm font-normal leading-heading text-grey-900 hover:bg-grey-100 dark:border-border dark:bg-muted dark:text-foreground dark:hover:bg-muted/80 dark:hover:text-foreground"
              >
                <Filter
                  className="h-4 w-4 shrink-0 text-grey-500 dark:text-muted-foreground"
                  strokeWidth={1.75}
                />
                {t(selectedStatusOption.labelKey, {
                  defaultValue: selectedStatusOption.defaultLabel,
                })}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-40">
              <DropdownMenuRadioGroup
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as ScheduledJobFilter)}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value}>
                    {t(option.labelKey, { defaultValue: option.defaultLabel })}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-6">
          <ChatInput
            onSendMessage={handleSchedule}
            isLoading={inviteAssistantMutation.isPending}
            inputButtons={inputButtons}
            isCurrentResponseGenerating={false}
            isFeatureSelected={isFeatureSelected}
            selectedSources={selectedSources}
            onSourcesChange={setSelectedSources}
            selectedFolderIdsBySource={selectedFolderIdsBySource}
            onSelectedFolderIdsBySourceChange={setSelectedFolderIdsBySource}
            isSourcesToolActive={isSourcesToolActive}
            onToggleTool={(toolId) => {
              setIsFeatureSelected((prev) => ({
                ...prev,
                [toolId]: !prev[toolId],
              }));
            }}
            onToggleSourcesTool={() => setIsSourcesToolActive((prev) => !prev)}
            isCancelOnGoing={false}
            isModelSelectionDisabled
            selectedTools={selectedTools}
            onSelectedToolsChange={setSelectedTools}
            showQuickActions={false}
            selectedAgentId={selectedAgentId}
            onAgentChange={(agentId) => setSelectedAgentId(agentId)}
            showAgentSelector
            showAgentProjectTab
            agentModelLabel={agentModelLabel}
            agentModelLoading={agentModelLoading}
            agentModelRegion={agentUploadModel?.region}
            agentUploadModel={agentUploadModel}
          />
        </div>

        <div className="mt-6 flex flex-col gap-4 border-b border-grey-100 pb-8 dark:border-border">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold leading-heading text-grey-900 dark:text-foreground">
              {t('RECENT', { defaultValue: 'Recent' })}
            </p>
            {isRecentFetching && !isRecentLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : null}
          </div>
          <ScheduledJobList
            jobs={recentJobs}
            isLoading={isRecentLoading}
            isError={isRecentError}
            emptyMessage={t('NO_RECENT_ITEMS', { defaultValue: 'No recent items.' })}
          />
        </div>

        <div className="mt-8 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold leading-heading text-grey-900 dark:text-foreground">
              {t(selectedStatusOption.labelKey, {
                defaultValue: selectedStatusOption.defaultLabel,
              })}
            </p>
            {isStatusFetching && !isStatusLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : null}
          </div>
          <ScheduledJobList
            jobs={statusJobs}
            isLoading={isStatusLoading}
            isError={isStatusError}
            emptyMessage={
              statusFilter === 'all'
                ? t('NO_SCHEDULED_JOBS', { defaultValue: 'No scheduled jobs.' })
                : t('NO_SCHEDULED_JOBS_FOR_STATUS', {
                    defaultValue: 'No {{status}} items.',
                    status: t(selectedStatusOption.labelKey, {
                      defaultValue: selectedStatusOption.defaultLabel,
                    }).toLowerCase(),
                  })
            }
          />
        </div>
      </div>

      <LinkedScheduledJobDialog />
    </div>
  );
}
